import React, { useEffect, useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import PerfectScrollbar from 'react-perfect-scrollbar'

import { Loading, ModalAsDropdown, ModalStyles } from 'components/shared'
import DidSelect from './DidSelect'
import { getDids } from 'store/actions/global'
import { setDid } from 'store/actions/profile'
import Flag from 'react-world-flags'
import { ReactComponent as CheckBlueIcon } from 'assets/svg/shared/check_blue.svg'
import { useTranslation } from 'react-i18next'
import { usePrevious } from 'hooks'

export default function DidsModal() {
    const { t } = useTranslation()
    const scrollContainerRef = useRef(null)
    const dispatch = useDispatch()

    const { did, didLoaded } = useSelector((store) => store.profile)
    const { caller_id_number: callerIdNumber } = useSelector((store) => store.profile.data)
    const { didsData, didsDataLoaded, didsPagination: pagination } = useSelector((store) => store.global)

    const notSelectedItem = { dialplan_uuid: 0, destination_number: t('nothing_selected') }
    const callerIdNumberItem = callerIdNumber ? { dialplan_uuid: 0, destination_number: callerIdNumber } : null

    const { loading, page, totalPages } = pagination

    // State
    const [selectedValue, setSelectedValue] = useState(null)
    const [isModalVisible, setModalVisible] = useState(false)
    const [modalStyles, setModalStyles] = useState(ModalStyles)
    const [isUpdateInProgress, setUpdateInProgress] = useState(true)
    const [isHidden, setIsHidden] = useState(false)

    const prevValue = usePrevious(selectedValue)

    // get did numbers data
    useEffect(() => {
        if (didsData.length === 0 && page === 1) {
            dispatch(getDids({ params: { page: 1 } }))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [didsData, page])

    // Set selected value when data loaded and set spinner off
    useEffect(() => {
        if (didLoaded && didsDataLoaded) {
            setSelectedValue(did || callerIdNumberItem || notSelectedItem)
            setUpdateInProgress(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [didLoaded, didsDataLoaded, did])

    // Hide select completely when no item is selected and there's no data to select from
    useEffect(() => {
        if (selectedValue?.destination_number === notSelectedItem.destination_number && didsData.length === 0) {
            setIsHidden(true)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedValue, didsData])

    // Actions
    function selectItem(item) {
        if (selectedValue.destination_number !== item.destination_number) {
            dispatch(setDid({ ...item, prevValue }))
        }
        toggleModal()
    }

    function toggleModal() {
        const appContainerHeight = +sessionStorage.getItem('appHeight')
        const windowHeight = window.innerHeight
        const bottomRetreat = (windowHeight - appContainerHeight) / 2
        setModalStyles({
            ...modalStyles,
            content: {
                ...modalStyles.content,
                bottom: `${bottomRetreat + 24}px`,
                maxHeight: '400px'
            }
        })

        if (didsData.length) {
            setModalVisible(!isModalVisible)
        }
    }

    function onYReachEnd() {
        if (!loading && page < totalPages) {
            dispatch(getDids({ params: { page: page + 1 } }))
        }
    }

    // Renders
    function modalContent() {
        return (
            <PerfectScrollbar ref={scrollContainerRef} style={{ maxHeight: '280px' }} onYReachEnd={onYReachEnd}>
                <div>
                    {didsData.map((config) => (
                        <div
                            key={config.dialplan_uuid}
                            className="dialpad__select__item d-flex align-items-center justify-content-between"
                            onClick={() => selectItem(config)}
                        >
                            <div className="dialpad__select__status d-flex align-items-center">
                                {config.code && <Flag code={config.code.toLowerCase()} height="12" />}
                                {config?.destination_additionals?.did_title && (
                                    <span className="dialpad__select__text-truncate mr-2">
                                        {config.destination_additionals.did_title}
                                    </span>
                                )}
                                <span>{config.destination_number}</span>
                            </div>
                            {selectedValue?.destination_number === config.destination_number && (
                                <CheckBlueIcon width={18} className="flex-shrink-0" />
                            )}
                        </div>
                    ))}
                    {loading && <Loading />}
                </div>
            </PerfectScrollbar>
        )
    }

    return (
        <>
            <DidSelect
                toggleModal={toggleModal}
                selectedItem={selectedValue}
                isUpdateInProgress={isUpdateInProgress}
                isDataEmpty={!didsData.length}
                isHidden={isHidden}
            />
            <ModalAsDropdown
                isModalVisible={isModalVisible}
                toggleModal={toggleModal}
                modalContent={modalContent}
                customModalStyles={modalStyles}
            />
        </>
    )
}
